// Session transport is independent of React and of any particular LLM provider.
// UI requests are allowlisted; model output can never invoke arbitrary app code.
type InvoiceControlRecord = Record<string, any>
type InvoiceControlEditor = {
  version: number
  snapshot: () => InvoiceControlRecord
  apply: (invoice: InvoiceControlRecord, options?: { partial: boolean }) => boolean
  setLayout: (layout: string) => void
  review: () => void
  save: () => Promise<unknown>
}

const invoiceControlApi = (() => {
  let editor: { businessId: string; adapter: InvoiceControlEditor } | null = null
  const registerEditor = (businessId: string, adapter: InvoiceControlEditor) => {
    const entry = { businessId, adapter }
    editor = entry
    return () => { if (editor === entry) editor = null }
  }
  const currentEditor = (businessId: string) => editor?.businessId === businessId ? editor.adapter : null
  const createSession = (deps: InvoiceControlRecord) => {
    let generation = 0
    let disposed = false
    let locked = false
    let pending: InvoiceControlRecord | null = null
    let state: InvoiceControlRecord = { messages: [], busy: false, error: '', ready: false, saved: false, review: false }
    const listeners = new Set<() => void>()
    const publish = (patch: InvoiceControlRecord) => {
      state = { ...state, ...patch }
      listeners.forEach((listener) => listener())
    }
    const active = (token: number) => !disposed && token === generation && deps.isCurrent()
    const waitForEditor = async (token: number) => {
      for (let attempt = 0; attempt < 100 && active(token); attempt++) {
        const found = currentEditor(deps.businessId)
        if (found) return found
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
      throw new Error('control.editorUnavailable')
    }
    const stop = () => {
      if (locked) return // A submitted save must retain its completion/error receipt.
      // The read-only provider request may finish, but its result loses authority.
      generation++
      publish({ busy: false, error: '', review: false })
    }
    const ask = async (question: string) => {
      if (disposed || locked || state.busy || !question.trim() || !deps.isCurrent()) return
      const token = ++generation
      const requestRoute = deps.getRoute()
      const requestEditor = requestRoute === 'faktura' ? currentEditor(deps.businessId) : null
      const before = requestEditor?.snapshot()
      const history = state.messages.slice(-10)
      const draft = before?.hasContent ? before.payload : pending
      publish({ busy: true, error: '', ready: false, saved: false, review: false,
        messages: [...state.messages, { role: 'user', content: question }].slice(-30) })
      try {
        const reply = await deps.ask(question, {
          businessId: deps.businessId, year: deps.year, locale: deps.locale(), history,
          source: 'plumme-invoice-control',
          workflowContext: { kind: 'invoice_control', stage: deps.getRoute(), layout: before?.layout, fiscalYear: deps.year },
          pendingIntentName: draft ? 'create_invoice' : undefined,
          pendingExecutionPayload: draft,
        })
        if (!active(token)) return
        if (deps.getRoute() !== requestRoute) throw new Error('control.editorChanged')
        if (reply.metadata?.sourceType !== 'agent') throw new Error('control.agentUnavailable')
        const payload = reply.metadata?.executionPayload
        if (payload && payload.action !== 'create_invoice') throw new Error('control.invoiceOnly')
        const view = reply.metadata?.workspace?.view
        const layout = reply.metadata?.workspace?.layout
        if (view && !['editor', 'list', 'save_draft', 'review'].includes(view)) throw new Error('control.invoiceOnly')
        if (layout && !['onepage', 'steps'].includes(layout)) throw new Error('control.invoiceOnly')
        const receipts: string[] = []
        if (payload) pending = payload
        if (payload || ['editor', 'save_draft', 'review'].includes(view)) {
          if (deps.getRoute() !== 'faktura') deps.navigate('faktura')
          const target = await waitForEditor(token)
          if (!active(token)) return
          if (target.version !== 2) throw new Error('control.reloadRequired')
          if (payload) {
            const latest = target.snapshot()
            if ((before && (target !== requestEditor || latest.key !== before.key)) || (!before && latest.hasContent)) throw new Error('control.editorChanged')
            const applied = target.apply(payload.normalizedInvoice, { partial: !payload.readyForExecution })
            if (applied) receipts.push(deps.translate(payload.readyForExecution ? 'control.fieldsApplied' : 'control.customerApplied'))
            publish({ ready: payload.readyForExecution === true && applied })
          }
          if (layout) { target.setLayout(layout); receipts.push(deps.translate(layout === 'onepage' ? 'control.onepageApplied' : 'control.stepsApplied')) }
          else if (view === 'editor') receipts.push(deps.translate('control.editorOpened'))
          if (view === 'review') { target.review(); publish({ review: true }) }
          if (view === 'review') receipts.push(deps.translate('control.reviewHelp'))
          if (view === 'save_draft') { publish({ ready: true }); receipts.push(deps.translate('control.saveReady')) }
        } else if (view === 'list') {
          const latest = currentEditor(deps.businessId)?.snapshot()
          if (requestRoute === 'faktura' && latest?.hasContent && latest.dirty) throw new Error('control.saveBeforeLeaving')
          pending = null
          deps.navigate('fakturaliste')
          receipts.push(deps.translate('control.listOpened'))
        }
        const explanation = payload && !payload.readyForExecution
          ? deps.translate('control.proposalIncomplete')
          : !view && !payload ? (reply.text || reply.answer || '') : ''
        const content = [...receipts, explanation].filter(Boolean).join('\n') || deps.translate('control.noChange')
        publish({ messages: [...state.messages, { role: 'assistant', content }].slice(-30) })
      } catch (error) {
        if (active(token)) publish({ error: error instanceof Error ? error.message : 'control.failed' })
      } finally {
        if (active(token)) publish({ busy: false })
      }
    }
    const save = async () => {
      if (disposed || locked || state.busy || !deps.isCurrent()) return
      const target = currentEditor(deps.businessId)
      if (!target || deps.getRoute() !== 'faktura') { publish({ error: 'control.editorUnavailable' }); return }
      if (target.version !== 2) { publish({ error: 'control.reloadRequired' }); return }
      locked = true
      const token = ++generation
      publish({ busy: true, error: '', saved: false })
      try {
        await target.save()
        if (active(token)) publish({ saved: true, ready: false })
      } catch (error) {
        if (active(token)) publish({ error: error instanceof Error ? error.message : 'control.failed' })
      } finally {
        locked = false
        if (active(token)) publish({ busy: false })
      }
    }
    return { ask, save, stop, getSnapshot: () => state,
      subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } },
      dispose: () => { disposed = true; generation++; listeners.clear() },
    }
  }
  return { registerEditor, currentEditor, createSession }
})()
Object.assign(globalThis, { PlummeInvoiceControl: invoiceControlApi })
